import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { COMPANY_LOGO_SRC } from '../constants/branding';
import {
  CLIENT_PORTAL_CTA_CLASSNAME,
  CLIENT_PORTAL_HREF,
  CLIENT_PORTAL_PATH
} from '../constants/erpca';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({
    services: false,
    industries: false,
    resources: false
  });
  const [sectionNavigation, setSectionNavigation] = useState({
    services: [],
    industries: []
  });
  /** Which desktop mega-menu is open (pathname of parent, e.g. '/services'); null when closed. */
  const [desktopOpenDropdown, setDesktopOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setDesktopOpenDropdown(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const fetchNavigationSections = async () => {
      try {
        const response = await axios.get(`${API}/section-navigation`);
        setSectionNavigation({
          services: response?.data?.services || [],
          industries: response?.data?.industries || []
        });
      } catch (error) {
        console.error('Error fetching section navigation:', error);
      }
    };

    fetchNavigationSections();
  }, []);

  const serviceDropdownItems = sectionNavigation.services.length > 0
    ? sectionNavigation.services.map((item) => ({
      name: item.name,
      path: item.path || `/services#${item.anchor}`
    }))
    : [
      { name: 'Assurance Services', path: '/services#assurance-services' },
      { name: 'Transaction Advisory', path: '/services#transaction-advisory' },
      { name: 'Valuation Services', path: '/services#valuation-services' },
      { name: 'Internal Audit', path: '/services#internal-audit' },
      { name: 'Tax Advisory', path: '/services#tax-advisory' },
      { name: 'Financial Advisory', path: '/services#financial-advisory' },
      { name: 'GIFT-IFSCA', path: '/services#gift-ifsca' }
    ];

  const industryDropdownItems = sectionNavigation.industries.length > 0
    ? sectionNavigation.industries.map((item) => ({
      name: item.name,
      path: item.path || `/practice-areas#${item.anchor}`
    }))
    : [
      { name: 'Infrastructure & Construction', path: '/practice-areas#infrastructure-construction' },
      { name: 'Corporate Sector', path: '/practice-areas#corporate-sector' },
      { name: 'Manufacturing', path: '/practice-areas#manufacturing' },
      { name: 'Real Estate & Development', path: '/practice-areas#real-estate-development' },
      { name: 'Technology & Startups', path: '/practice-areas#technology-startups' },
      { name: 'Healthcare', path: '/practice-areas#healthcare' },
      { name: 'Education & Non-Profits', path: '/practice-areas#education-non-profits' },
      { name: 'Trading & Distribution', path: '/practice-areas#trading-distribution' }
    ];

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      hasDropdown: true,
      dropdownItems: serviceDropdownItems
    },
    { 
      name: 'Industries Serve', 
      path: '/practice-areas',
      hasDropdown: true,
      dropdownItems: industryDropdownItems
    },
    { 
      name: 'Resources', 
      path: '/resources',
      hasDropdown: true,
      dropdownItems: [
        { name: 'Blog', path: '/resources#blog' },
        { name: 'Newsletter', path: '/resources#newsletter' },
        { name: 'Corporate Deck', path: '/resources#corporate-deck' }
      ]
    },
    { name: 'Contact', path: '/contact#send-us-a-message' }
  ];

  const contactNav = navLinks.find((l) => l.name === 'Contact');
  const navWithoutContact = navLinks.filter((l) => l.name !== 'Contact');

  const isActive = (path) => location.pathname === path;
  const toggleMobileDropdown = (key) => {
    setMobileDropdownOpen((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleLogoClick = (event) => {
    event.preventDefault();
    navigate('/');

    // Ensure scroll reset also works when user is already on "/".
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const handleHomeNavigation = (event, closeMobileMenu = false) => {
    handleLogoClick(event);
    if (closeMobileMenu) {
      setMobileMenuOpen(false);
    }
  };

  const scrollToSectionByHash = (hash) => {
    if (!hash) return;
    const target = document.getElementById(hash);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    target.classList.add('ring-4', 'ring-[#1e3a8a]/40', 'ring-offset-2', 'transition-all', 'duration-700');
    window.setTimeout(() => {
      target.classList.remove('ring-4', 'ring-[#1e3a8a]/40', 'ring-offset-2', 'transition-all', 'duration-700');
    }, 1800);
  };

  const handleSectionNavigation = (event, path, closeMobileMenu = false) => {
    const [targetPath, rawHash] = path.split('#');
    const hash = rawHash ? decodeURIComponent(rawHash) : '';

    if (closeMobileMenu) {
      setMobileMenuOpen(false);
    }

    // If already on target page, force smooth-scroll immediately.
    if (location.pathname === targetPath && hash) {
      event.preventDefault();
      if (location.hash !== `#${hash}`) {
        navigate(`${targetPath}#${hash}`, { replace: true });
      }
      scrollToSectionByHash(hash);
      return;
    }

    // Header-only scroll reset: full routes without hash (About, Services index, etc.).
    // Hash links rely on HashScrollHandler for in-page targets.
    if (!hash) {
      queueMicrotask(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
    }
  };

  const linkBaseClass = 'text-base font-medium transition-colors';
  const linkInactiveClass = 'text-gray-600 hover:text-[#1e3a8a]';
  const linkActiveClass = 'text-[#1e3a8a]';

  return (
    <header aria-label="Site header" className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" onClick={handleLogoClick} className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-4">
            <img
              src={COMPANY_LOGO_SRC}
              alt="PFA AND ASSOCIATES LLP"
              className="h-9 w-auto max-h-9 max-w-[min(100vw-8rem,300px)] shrink-0 object-contain object-left sm:h-11 sm:max-h-11 md:h-12 md:max-h-12"
              decoding="async"
            />
            <div className="border-l-2 border-gray-300 h-12 mx-2"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_valueaudit/artifacts/65jffkfh_CA%20India%20Logo.png"
              alt="ICAI - Institute of Chartered Accountants of India"
              className="h-14"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav aria-label="Primary navigation" className="hidden lg:flex items-center gap-8">
            {navWithoutContact.map((link) => (
              <div
                key={link.path}
                className="relative"
                onMouseEnter={() => link.hasDropdown && setDesktopOpenDropdown(link.path)}
                onMouseLeave={() => link.hasDropdown && setDesktopOpenDropdown(null)}
              >
                {link.hasDropdown ? (
                  <>
                    <Link
                      to={link.path}
                      onClick={(event) => handleSectionNavigation(event, link.path)}
                      className={`${linkBaseClass} flex items-center gap-1 ${
                        isActive(link.path) ? linkActiveClass : linkInactiveClass
                      }`}
                    >
                      {link.name}
                      <ChevronDown size={16} />
                    </Link>
                    <div
                      className={`absolute left-0 top-full z-50 w-56 pt-2 -mt-2 transition-all duration-200 ${
                        desktopOpenDropdown === link.path
                          ? 'visible opacity-100'
                          : 'invisible pointer-events-none opacity-0'
                      }`}
                    >
                      <div className="rounded-lg border border-gray-200 bg-white py-2 shadow-lg">
                        {link.dropdownItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={(event) => {
                              handleSectionNavigation(event, item.path);
                              setDesktopOpenDropdown(null);
                            }}
                            className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-100 hover:text-[#1e3a8a]"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(event) =>
                    link.path === '/' ? handleHomeNavigation(event) : handleSectionNavigation(event, link.path)
                  }
                  className={`${linkBaseClass} ${
                    isActive(link.path) ? linkActiveClass : linkInactiveClass
                  }`}
                >
                  {link.name}
                </Link>
                )}
              </div>
            ))}
            {contactNav ? (
              <Link
                to={contactNav.path}
                onClick={(event) => handleSectionNavigation(event, contactNav.path)}
                className={`${linkBaseClass} ${
                  isActive('/contact') ? linkActiveClass : linkInactiveClass
                }`}
              >
                {contactNav.name}
              </Link>
            ) : null}
            <Link
              to={CLIENT_PORTAL_HREF}
              onClick={(event) => handleSectionNavigation(event, CLIENT_PORTAL_HREF)}
              className={`${CLIENT_PORTAL_CTA_CLASSNAME} h-12 shrink-0 px-6 ${
                isActive(CLIENT_PORTAL_PATH) ? 'ring-2 ring-[#1e3a8a]/35 ring-offset-2 ring-offset-white' : ''
              }`}
            >
              Login
              <ArrowRight
                className="ml-2 size-[18px] transition-transform group-hover:translate-x-0.5"
                aria-hidden
              />
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <nav aria-label="Mobile navigation" className="flex flex-col gap-4">
              {navWithoutContact.map((link) => {
                if (!link.hasDropdown) {
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-base font-medium transition-colors ${
                        isActive(link.path)
                          ? 'text-[#1e3a8a]'
                          : 'text-gray-600 hover:text-[#1e3a8a]'
                      }`}
                      onClick={(event) => {
                        if (link.path === '/') {
                          handleHomeNavigation(event, true);
                          return;
                        }
                        handleSectionNavigation(event, link.path, true);
                      }}
                    >
                      {link.name}
                    </Link>
                  );
                }

                const dropdownKey = link.path === '/services'
                  ? 'services'
                  : link.path === '/practice-areas'
                    ? 'industries'
                    : 'resources';

                return (
                  <div key={link.path} className="space-y-2">
                    <button
                      type="button"
                      onClick={() => toggleMobileDropdown(dropdownKey)}
                      className={`w-full text-left text-base font-medium transition-colors flex items-center justify-between ${
                        isActive(link.path)
                          ? 'text-[#1e3a8a]'
                          : 'text-gray-600 hover:text-[#1e3a8a]'
                      }`}
                    >
                      <span>{link.name}</span>
                      <ChevronDown
                        size={16}
                        className={`transition-transform ${mobileDropdownOpen[dropdownKey] ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {mobileDropdownOpen[dropdownKey] && (
                      <div className="pl-4 space-y-2 border-l border-gray-200">
                        {link.dropdownItems.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            className="block text-sm text-gray-600 hover:text-[#1e3a8a] transition-colors"
                            onClick={(event) => {
                              setMobileDropdownOpen((prev) => ({
                                ...prev,
                                [dropdownKey]: false
                              }));
                              handleSectionNavigation(event, item.path, true);
                            }}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {contactNav ? (
                <Link
                  to={contactNav.path}
                  className={`text-base font-medium transition-colors ${
                    isActive('/contact')
                      ? 'text-[#1e3a8a]'
                      : 'text-gray-600 hover:text-[#1e3a8a]'
                  }`}
                  onClick={(event) => {
                    handleSectionNavigation(event, contactNav.path, true);
                  }}
                >
                  {contactNav.name}
                </Link>
              ) : null}
              <Link
                to={CLIENT_PORTAL_HREF}
                className={`${CLIENT_PORTAL_CTA_CLASSNAME} h-12 w-full ${
                  isActive(CLIENT_PORTAL_PATH) ? 'ring-2 ring-white/40 ring-offset-2 ring-offset-transparent' : ''
                }`}
                onClick={(event) => handleSectionNavigation(event, CLIENT_PORTAL_HREF, true)}
              >
                Login
                <ArrowRight
                  className="ml-2 size-[18px] transition-transform group-hover:translate-x-0.5"
                  aria-hidden
                />
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
